import { type MouseEvent, useState } from 'react';

import type { Heading } from '@/api/client';
import { useT } from '@/i18n/LocaleProvider';

export type TocNode = {
  id: string;
  text: string;
  level: number;
  children: TocNode[];
};

export type TableOfContentsProps = {
  headings: Heading[];
  title?: string;
};

export function buildTocTree(headings: Heading[]): TocNode[] {
  const roots: TocNode[] = [];
  const stack: TocNode[] = [];

  for (const heading of headings) {
    const node: TocNode = {
      id: heading.id,
      text: heading.text,
      level: heading.level,
      children: [],
    };

    while (stack.length > 0 && stack[stack.length - 1].level >= node.level) {
      stack.pop();
    }

    const parent = stack[stack.length - 1];

    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }

    stack.push(node);
  }

  return roots;
}

export function TableOfContents({ headings, title }: TableOfContentsProps) {
  const t = useT();
  const [collapsed, setCollapsed] = useState(false);
  const displayTitle = title ?? t('toc_title');

  if (headings.length < 3) {
    return null;
  }

  return (
    <nav className="toc" aria-label={displayTitle}>
      <div className="toc-header">
        <span>{displayTitle}</span>
        <button
          type="button"
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((value) => !value)}
        >
          {collapsed ? t('toc_show') : t('toc_hide')}
        </button>
      </div>

      {collapsed ? null : <TocList nodes={buildTocTree(headings)} />}
    </nav>
  );
}

type TocListProps = {
  nodes: TocNode[];
};

function TocList({ nodes }: TocListProps) {
  return (
    <ol>
      {nodes.map((node) => (
        <li key={node.id}>
          <a href={`#${node.id}`} onClick={(event) => scrollToHeading(event, node.id)}>
            {node.text}
          </a>
          {node.children.length > 0 ? <TocList nodes={node.children} /> : null}
        </li>
      ))}
    </ol>
  );
}

function scrollToHeading(event: MouseEvent<HTMLAnchorElement>, id: string) {
  event.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
}
